import React, { useState } from 'react';
import { Link } from 'react-router';
import { ChildProgress } from './ChildProgress';
import { ChildSubscriptionModal } from './ChildSubscriptionModal';
import { SubscriptionHistoryModal } from './SubscriptionHistoryModal';
import { SUBSCRIPTION_COLORS } from './subscriptionColors';
import { CopyableInput } from '../../../shared/components/CopyableInput';

// Helper to get the kid login URL based on current domain
const getKidLoginUrl = () => {
  const { protocol, hostname, port } = window.location;
  let kidHost;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    kidHost = 'kid.lvh.me';
  } else if (hostname.startsWith('kid.')) {
    kidHost = hostname;
  } else {
    kidHost = `kid.${hostname}`;
  }
  const portPart = port ? `:${port}` : '';
  return `${protocol}//${kidHost}${portPart}`;
};

export const Children = ({ children, family, t, formatNumber, formatDate }) => {
  const [selectedChild, setSelectedChild] = useState(null);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionModalMode, setSubscriptionModalMode] = useState('subscribe');
  const [showProgressRestrictionModal, setShowProgressRestrictionModal] = useState(false);
  const [showSubscriptionHistoryModal, setShowSubscriptionHistoryModal] = useState(false);

  const handleApprove = (path) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = path;
    form.setAttribute('data-turbo', 'true');

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    if (csrfToken) {
      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'authenticity_token';
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);
    }

    document.body.appendChild(form);
    form.submit();
  };

  const handleReject = (path) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = path;
    form.setAttribute('data-turbo', 'true');

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    if (csrfToken) {
      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'authenticity_token';
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);
    }

    document.body.appendChild(form);
    form.submit();
  };

  const handleDelete = (child) => {
    if (!confirm(t('dashboard.index.children.confirm_remove', { name: child.username }))) {
      return;
    }

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = child.delete_path;
    form.setAttribute('data-turbo', 'true');

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    if (csrfToken) {
      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'authenticity_token';
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);
    }

    const methodInput = document.createElement('input');
    methodInput.type = 'hidden';
    methodInput.name = '_method';
    methodInput.value = 'delete';
    form.appendChild(methodInput);

    document.body.appendChild(form);
    form.submit();
  };

  const handleSubscribeClick = (child) => {
    setSelectedChild(child);
    setSubscriptionModalMode('subscribe');
    setShowSubscriptionModal(true);
  };

  const handleExtendClick = (child) => {
    setSelectedChild(child);
    setSubscriptionModalMode('extend');
    setShowSubscriptionModal(true);
  };

  const handleUpgradeClick = (child) => {
    setSelectedChild(child);
    setSubscriptionModalMode('upgrade');
    setShowSubscriptionModal(true);
  };

  const handleSubscriptionHistoryClick = (child) => {
    setSelectedChild(child);
    setShowSubscriptionHistoryModal(true);
  };

  const handleExtendFromHistory = () => {
    setShowSubscriptionHistoryModal(false);
    setSubscriptionModalMode('extend');
    setShowSubscriptionModal(true);
  };

  const handleSubscribeFromHistory = () => {
    setShowSubscriptionHistoryModal(false);
    setSubscriptionModalMode('subscribe');
    setShowSubscriptionModal(true);
  };

  const handleUpgradeFromHistory = () => {
    setShowSubscriptionHistoryModal(false);
    setSubscriptionModalMode('upgrade');
    setShowSubscriptionModal(true);
  };

  // Get subscription status: 'not_activated', 'active', 'expiring_soon', 'expired'
  const getSubscriptionStatus = (child) => {
    if (!child.subscribed) {
      return 'not_activated';
    }

    const expirationDate = child.subscription_expired_on ? new Date(child.subscription_expired_on) : null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (expirationDate && expirationDate < today) {
      return 'expired';
    }

    if (child.subscription_expiring_soon) {
      return 'expiring_soon';
    }

    return 'active';
  };

  // Get avatar background color based on subscription status
  const getAvatarBgColor = (status) => {
    switch (status) {
      case 'not_activated':
        return '#ffedd5'; // Orange-100
      case 'expired':
        return '#fee2e2'; // Red-100
      case 'expiring_soon':
        return '#fef9c3'; // Yellow-100
      case 'active':
        return '#dbeafe'; // Blue-100
      default:
        return '#e5e7eb'; // Gray-200
    }
  };

  // Render the subscription tier badge (Free/Pro/Premium)
  const renderTierBadge = (child) => {
    const status = getSubscriptionStatus(child);
    const isFree = status === 'not_activated';
    const isPremium = child.subscription_package === 'premium';

    // Free = gray, Pro = green, Premium = yellow
    let badgeStyle, badgeLabel;
    if (isFree) {
      badgeStyle = SUBSCRIPTION_COLORS.free.badge;
      badgeLabel = t('common.badge_free');
    } else if (isPremium) {
      badgeStyle = SUBSCRIPTION_COLORS.premium.badge;
      badgeLabel = t('common.badge_premium') || 'Premium';
    } else {
      badgeStyle = SUBSCRIPTION_COLORS.pro.badge;
      badgeLabel = t('common.badge_pro');
    }

    return (
      <span
        className="badge d-inline-block px-2 py-1 rounded-pill fw-semibold"
        style={{ fontSize: '12px', ...badgeStyle }}
        data-testid="subscription-tier-badge"
      >
        {badgeLabel}
      </span>
    );
  };

  // Render subscription section with gradient background
  const renderSubscriptionSection = (child) => {
    const status = getSubscriptionStatus(child);

    // Use subtle backgrounds that match the parent card's bg-light styling
    const sectionStyles = {
      not_activated: {
        background: '#f8f9fa',
        border: '1px solid #e9ecef'
      },
      expired: {
        background: '#f8f9fa',
        border: '1px solid #e9ecef'
      },
      expiring_soon: {
        background: '#f8f9fa',
        border: '1px solid #e9ecef'
      },
      active: {
        background: '#f8f9fa',
        border: '1px solid #e9ecef'
      }
    };

    // Use Bootstrap-consistent button colors - Subscribe/extend buttons are green (Pro button color)
    const buttonStyles = {
      not_activated: SUBSCRIPTION_COLORS.pro.button,  // Green for activate
      expired: SUBSCRIPTION_COLORS.pro.button,  // Green for renew
      expiring_soon: SUBSCRIPTION_COLORS.pro.button,  // Green for renew
      active: SUBSCRIPTION_COLORS.pro.button  // Green (not shown for active)
    };

    const expiryTextStyles = {
      expired: { color: '#dc2626' },
      expiring_soon: { color: '#a16207' },
      active: { color: '#1f2937' }
    };

    const style = sectionStyles[status] || sectionStyles.not_activated;
    const buttonStyle = buttonStyles[status] || buttonStyles.not_activated;
    const expiryStyle = expiryTextStyles[status] || { color: '#1f2937' };

    const isNotActivated = status === 'not_activated';
    const buttonText = isNotActivated
      ? t('dashboard.index.children.subscription.upgrade')
      : t('dashboard.index.children.subscription.renew');

    return (
      <div
        className="rounded-3 p-3"
        style={{ ...style }}
      >
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          {/* Left side: Label and tier badge */}
          <div className="d-flex align-items-center gap-2 flex-wrap" style={{ fontSize: '12px' }}>
            <i className="bi bi-award-fill" style={{ color: '#374151', fontSize: '14px' }}></i>
            <span className="fw-semibold" style={{ color: '#374151' }}>
              {t('dashboard.index.children.subscription.subscription_label')}:
            </span>
            {renderTierBadge(child)}
            {/* Show expiry date for subscribed kids */}
            {!isNotActivated && child.subscription_expired_on && (
              <span className="d-inline-flex align-items-center gap-2" style={{ whiteSpace: 'nowrap' }}>
                <i className="bi bi-calendar3" style={{ fontSize: '14px', color: '#374151' }}></i>
                <span className="fw-semibold" style={{ color: '#374151' }}>
                  {t('dashboard.index.children.subscription.expires_on')}
                </span>
                <span
                  className={`fw-semibold ${status === 'active' ? 'text-success' : status === 'expired' ? 'text-danger' : 'text-warning'}`}
                  style={{ ...expiryStyle, cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => handleSubscriptionHistoryClick(child)}
                  title={t('dashboard.index.children.subscription.click_to_view_history') || 'Click to view subscription history'}
                  data-testid="subscription-date-link"
                >
                  {formatDate(child.subscription_expired_on)}
                </span>
              </span>
            )}
          </div>
          {/* Right side: Action buttons */}
          <div className="d-flex gap-2">
            {/* Upgrade to Premium button - only show for active Pro subscribers */}
            {status === 'active' && child.can_upgrade_to_premium && (
              <button
                onClick={() => handleUpgradeClick(child)}
                className="btn btn-sm d-flex align-items-center gap-1 fw-semibold"
                style={{
                  ...SUBSCRIPTION_COLORS.premium.button,
                  borderRadius: '8px',
                  fontSize: '12px',
                  padding: '6px 12px',
                  border: 'none',
                  boxShadow: '0px 1px 2px rgba(0,0,0,0.05)'
                }}
                data-testid="upgrade-premium-button"
              >
                <i className="bi bi-arrow-up" style={{ fontSize: '12px' }}></i>
                {t('dashboard.index.children.subscription.upgrade_premium') || 'Premium'}
              </button>
            )}
            {/* Subscribe/Renew button - only show when not active */}
            {status !== 'active' && (
              <button
                onClick={() => isNotActivated ? handleSubscribeClick(child) : handleExtendClick(child)}
                className="btn btn-sm d-flex align-items-center gap-1 fw-semibold"
                style={{
                  ...buttonStyle,
                  borderRadius: '8px',
                  fontSize: '12px',
                  padding: '6px 12px',
                  border: 'none',
                  boxShadow: '0px 1px 2px rgba(0,0,0,0.05)'
                }}
                data-testid="subscription-action-button"
              >
                <i className="bi bi-plus-lg" style={{ fontSize: '12px' }}></i>
                {buttonText}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Handle progress button click
  // Only Premium subscribers can view detailed progress
  const handleProgressClick = (child) => {
    const hasPremium = child.has_active_subscription && child.subscription_package === 'premium';

    if (hasPremium) {
      setSelectedChild(child);
      setShowProgressModal(true);
    } else {
      setSelectedChild(child);
      setShowProgressRestrictionModal(true);
    }
  };

  // Handle action from progress restriction modal
  // Free kids -> Subscribe, Pro kids -> Upgrade to Premium
  const handleActionFromRestriction = () => {
    setShowProgressRestrictionModal(false);
    if (selectedChild?.has_active_subscription) {
      // Pro subscriber - upgrade to Premium
      setSubscriptionModalMode('upgrade');
    } else {
      // Free - subscribe
      setSubscriptionModalMode('subscribe');
    }
    setShowSubscriptionModal(true);
  };

  // Render action buttons based on subscription status and curriculum
  const renderActionButtons = (child) => {
    const hasCurriculum = child.has_curriculum;

    const buttons = [];

    // Progress button - shown when child has curriculum
    if (hasCurriculum) {
      buttons.push(
        <button
          key="progress"
          data-testid="progress-button"
          onClick={() => handleProgressClick(child)}
          className="btn btn-sm d-flex align-items-center justify-content-center gap-2 fw-semibold"
          style={{
            background: '#faf5ff',
            color: '#9333ea',
            borderRadius: '12px',
            fontSize: '14px',
            padding: '10px 16px',
            border: 'none',
            flex: '1 1 calc(50% - 6px)'
          }}
        >
          <i className="bi bi-graph-up" style={{ fontSize: '14px' }}></i>
          {t('dashboard.index.children.subscription.progress_button')}
        </button>
      );
    }

    // Target button - only when child has curriculum
    if (hasCurriculum) {
      buttons.push(
        <Link
          key="target"
          to={`/children/${child.id}/daily-target`}
          className="btn btn-sm d-flex align-items-center justify-content-center gap-2 fw-semibold text-decoration-none"
          style={{
            background: '#f0fdf4',
            color: '#16a34a',
            borderRadius: '12px',
            fontSize: '14px',
            padding: '10px 16px',
            border: 'none',
            flex: '1 1 calc(50% - 6px)'
          }}
        >
          <i className="bi bi-bullseye" style={{ fontSize: '14px' }}></i>
          {t('dashboard.index.children.target')}
        </Link>
      );
    }

    // Curriculum/Study button - always shown
    buttons.push(
      <Link
        key="curriculum"
        to={`/children/${child.id}/curriculum`}
        className="btn btn-sm d-flex align-items-center justify-content-center gap-2 fw-semibold text-decoration-none"
        style={{
          background: '#eff6ff',
          color: '#2563eb',
          borderRadius: '12px',
          fontSize: '14px',
          padding: '10px 16px',
          border: 'none',
          flex: '1 1 calc(50% - 6px)'
        }}
      >
        <i className="bi bi-book" style={{ fontSize: '14px' }}></i>
        {t('dashboard.index.children.study')}
      </Link>
    );

    // Sign In button - only when child has curriculum
    if (hasCurriculum) {
      buttons.push(
        <button
          key="signin"
          onClick={() => { setSelectedChild(child); setShowSignInModal(true); }}
          className="btn btn-sm d-flex align-items-center justify-content-center gap-2 fw-semibold"
          style={{
            background: '#fff7ed',
            color: '#ea580c',
            borderRadius: '12px',
            fontSize: '14px',
            padding: '10px 16px',
            border: 'none',
            flex: '1 1 calc(50% - 6px)'
          }}
        >
          <i className="bi bi-box-arrow-in-right" style={{ fontSize: '14px' }}></i>
          {t('dashboard.index.children.sign_in')}
        </button>
      );
    }

    return (
      <div className="d-flex flex-wrap gap-2">
        {buttons}
      </div>
    );
  };

  const zodiacSprite = (avatar, zodiacName) => {
    return (
      <span
        className={`zodiac-icon zodiac-icon-${avatar} zodiac-icon-sm zodiac-icon-circle`}
        role="img"
        aria-label={zodiacName}
      ></span>
    );
  };

  return (
    <>
      {/* Header section */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h2 className="fw-bold mb-0" style={{ fontSize: '24px', color: '#111827' }}>
          {t('dashboard.index.children.title')}
        </h2>
        {children.length > 0 && (
          <Link
            to="/children/new"
            className="btn d-flex align-items-center justify-content-center rounded-3"
            aria-label={t('dashboard.index.children.add_child')}
            style={{
              width: '40px',
              height: '40px',
              background: '#a855f7',
              border: 'none',
              boxShadow: '0px 4px 6px #e9d5ff, 0px 10px 15px #e9d5ff'
            }}
          >
            <i className="bi bi-plus-lg text-white" style={{ fontSize: '18px' }}></i>
          </Link>
        )}
      </div>

      {children.length === 0 ? (
        <Link
          to="/children/new"
          className="bg-white rounded-4 p-3 text-decoration-none d-block"
          style={{
            boxShadow: '0px 4px 20px rgba(0,0,0,0.05)',
            border: '2px dashed #a855f7',
            minHeight: '200px'
          }}
        >
          <div className="d-flex flex-column align-items-center justify-content-center h-100" style={{ minHeight: '180px' }}>
            <div
              className="rounded-circle d-flex align-items-center justify-content-center mb-3"
              style={{
                width: '64px',
                height: '64px',
                background: '#f3e8ff',
                border: '2px solid #a855f7'
              }}
            >
              <i className="bi bi-plus-lg" style={{ fontSize: '28px', color: '#a855f7' }}></i>
            </div>
            <p className="text-muted mb-2">{t('dashboard.index.children.no_children')}</p>
            <h5 className="fw-bold mb-0" style={{ color: '#a855f7' }}>
              {t('dashboard.index.children.add_child')}
            </h5>
          </div>
        </Link>
      ) : (
        <div className="d-flex flex-column gap-4">
          {children.map((child) => {
            const status = getSubscriptionStatus(child);

            return (
              <article
                key={child.id}
                data-testid={`kid-${child.username}`}
                className="bg-white rounded-4 p-3"
                style={{
                  boxShadow: '0px 4px 20px rgba(0,0,0,0.05)',
                  border: '1px solid #f3f4f6'
                }}
              >
                {/* Child header section */}
                <div className="d-flex align-items-start justify-content-between mb-3">
                  <div className="d-flex align-items-start gap-3">
                    {/* Avatar */}
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: '56px',
                        height: '56px',
                        background: getAvatarBgColor(status),
                        border: '2px solid white',
                        boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
                        flexShrink: 0
                      }}
                    >
                      {zodiacSprite(child.avatar, child.zodiac_name)}
                    </div>
                    {/* Name and info */}
                    <div>
                      <h5 className="fw-bold mb-0" style={{ fontSize: '18px', color: '#111827' }}>
                        {child.username}
                      </h5>
                      <p className="mb-0" style={{ fontSize: '12px', color: '#6b7280' }}>
                        {child.age} {t('dashboard.index.children.age')}
                      </p>
                    </div>
                  </div>

                  {/* Menu buttons */}
                  <div className="d-flex align-items-center gap-1">
                    {/* 3-dots menu */}
                    <div className="dropdown">
                      <button
                        className="btn btn-sm btn-link text-secondary p-2"
                        type="button"
                        id={`childMenuDropdown${child.id}`}
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                        style={{ lineHeight: '1' }}
                      >
                        <i className="bi bi-three-dots-vertical" style={{ fontSize: '16px' }}></i>
                      </button>
                      <ul className="dropdown-menu dropdown-menu-end" aria-labelledby={`childMenuDropdown${child.id}`}>
                        <li>
                          <a href={child.edit_path} className="dropdown-item d-flex align-items-center">
                            <i className="bi bi-pencil me-2"></i>
                            {t('dashboard.index.children.menu.edit')}
                          </a>
                        </li>
                        <li>
                          <button
                            onClick={() => handleDelete(child)}
                            className="dropdown-item d-flex align-items-center text-danger"
                            style={{ border: 'none', background: 'none', width: '100%', textAlign: 'left' }}
                          >
                            <i className="bi bi-trash me-2"></i>
                            {t('dashboard.index.children.menu.delete')}
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Subscription section */}
                <div className="mb-3">
                  {renderSubscriptionSection(child)}
                </div>

                {/* XP Progress section */}
                <div
                  className="rounded-3 p-3 mb-3"
                  style={{ background: '#f9fafb' }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fw-semibold" style={{ fontSize: '12px', color: '#9ca3af' }}>
                      {t('dashboard.index.children.subscription.xp_progress')}
                    </span>
                    <span
                      className="fw-semibold"
                      style={{
                        fontSize: '12px',
                        color: status === 'active' ? '#16a34a' : '#a855f7'
                      }}
                    >
                      {formatNumber(child.achieved_xp)}/{formatNumber(child.target_xp)}
                    </span>
                  </div>
                  <div
                    className="rounded-pill progress-bar"
                    style={{ background: '#e5e7eb', height: '8px', width: '100%' }}
                  >
                    <div
                      className="rounded-pill"
                      style={{
                        background: status === 'active' ? '#22c55e' : '#a855f7',
                        height: '8px',
                        width: `${child.progress_percentage}%`,
                        boxShadow: status === 'active' ? '0px 1px 2px #bbf7d0' : 'none'
                      }}
                    ></div>
                  </div>
                </div>

                {/* Action buttons */}
                {renderActionButtons(child)}

                {/* Skip Request Banner */}
                {child.skip_requests && child.skip_requests.length > 0 && child.skip_requests.map((skipRequest) => (
                  <div
                    key={skipRequest.id}
                    className="mt-3 p-2 rounded-2 border skip-request-item"
                    style={{ backgroundColor: '#fef3c7', borderColor: '#fde68a' }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-2">
                        <i className="bi bi-exclamation-circle text-warning" style={{ fontSize: '16px' }}></i>
                        <span className="fw-medium text-warning-emphasis" style={{ fontSize: '14px' }}>
                          {t('dashboard.index.children.skip_request.title')}
                        </span>
                        <span className="text-muted" style={{ fontSize: '12px' }}>
                          - {skipRequest.activity_name}
                        </span>
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          onClick={() => {
                            if (confirm(t('dashboard.index.children.skip_request.confirm_approve'))) {
                              handleApprove(skipRequest.approve_path);
                            }
                          }}
                          className="btn btn-sm btn-success d-flex align-items-center gap-1"
                          style={{ fontSize: '12px', padding: '4px 12px' }}
                        >
                          <i className="bi bi-check-lg"></i>
                          {t('common.approve')}
                        </button>
                        <button
                          onClick={() => {
                            if (confirm(t('dashboard.index.children.skip_request.confirm_reject'))) {
                              handleReject(skipRequest.reject_path);
                            }
                          }}
                          className="btn btn-sm btn-danger d-flex align-items-center gap-1"
                          style={{ fontSize: '12px', padding: '4px 12px' }}
                        >
                          <i className="bi bi-x-lg"></i>
                          {t('common.reject')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </article>
            );
          })}
        </div>
      )}

      {/* Sign In Info Modal */}
      {showSignInModal && selectedChild && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title d-flex align-items-center gap-2">
                  <i className="bi bi-box-arrow-in-right text-primary"></i>
                  {t('dashboard.index.children.sign_in_modal.title', { name: selectedChild.username })}
                </h5>
                <button type="button" className="btn-close" onClick={() => { setShowSignInModal(false); setSelectedChild(null); }}></button>
              </div>
              <div className="modal-body">
                <p className="text-muted mb-4">{t('dashboard.index.children.sign_in_modal.description')}</p>

                {/* URL Field */}
                <CopyableInput
                  value={getKidLoginUrl()}
                  label={t('dashboard.index.children.sign_in_modal.url_label')}
                  icon={<i className="bi bi-link-45deg"></i>}
                  copyText={t('common.copy')}
                  copiedText={t('common.copied')}
                  className="mb-3"
                />

                {/* Username Field */}
                <CopyableInput
                  value={selectedChild.username}
                  label={t('dashboard.index.children.sign_in_modal.username_label')}
                  icon={<i className="bi bi-person"></i>}
                  copyText={t('common.copy')}
                  copiedText={t('common.copied')}
                  className="mb-3"
                />

                {/* Change Password Link */}
                <div className="border-top pt-3 mt-3">
                  <button
                    type="button"
                    className="btn btn-link p-0 d-flex align-items-center gap-2 text-decoration-none"
                    onClick={() => { setShowSignInModal(false); setShowPasswordModal(true); }}
                  >
                    <i className="bi bi-key"></i>
                    {t('dashboard.index.children.sign_in_modal.change_password_link')}
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => { setShowSignInModal(false); setSelectedChild(null); }}
                >
                  {t('common.close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {showPasswordModal && selectedChild && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('dashboard.index.children.change_password_modal.title', { name: selectedChild.username })}</h5>
                <button type="button" className="btn-close" onClick={() => { setShowPasswordModal(false); setSelectedChild(null); }}></button>
              </div>
              <div className="modal-body">
                <p className="text-muted">{t('dashboard.index.children.change_password_modal.description')}</p>
                <form id="changePasswordForm" action={`/children/${selectedChild.id}/update_password`} method="POST">
                  <input type="hidden" name="_method" value="patch" />
                  <input type="hidden" name="authenticity_token" value={document.querySelector('meta[name="csrf-token"]')?.content} />
                  <div className="mb-3">
                    <label htmlFor="new_password" className="form-label">{t('dashboard.index.children.change_password_modal.new_password_label')}</label>
                    <input type="password" className="form-control" id="new_password" name="child[password]" required minLength="6" placeholder={t('dashboard.index.children.change_password_modal.new_password_placeholder')} />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="confirm_password" className="form-label">{t('dashboard.index.children.change_password_modal.confirm_password_label')}</label>
                    <input type="password" className="form-control" id="confirm_password" name="child[password_confirmation]" required minLength="6" placeholder={t('dashboard.index.children.change_password_modal.confirm_password_placeholder')} />
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowPasswordModal(false); setSelectedChild(null); }}>{t('dashboard.index.children.change_password_modal.cancel')}</button>
                <button type="submit" form="changePasswordForm" className="btn btn-primary">{t('dashboard.index.children.change_password_modal.submit')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Progress Modal */}
      {showProgressModal && selectedChild && (
        <ChildProgress
          child={selectedChild}
          onClose={() => { setShowProgressModal(false); setSelectedChild(null); }}
          t={t}
          formatNumber={formatNumber}
          formatDate={formatDate}
        />
      )}

      {/* Subscription Modal */}
      {showSubscriptionModal && selectedChild && (
        <ChildSubscriptionModal
          child={selectedChild}
          family={family}
          mode={subscriptionModalMode}
          onClose={() => { setShowSubscriptionModal(false); setSelectedChild(null); }}
          t={t}
          formatNumber={formatNumber}
          activityCounts={family ? {
            freeActivitiesCount: family.free_activities_count,
            paidActivitiesCount: family.paid_activities_count,
          } : null}
        />
      )}

      {/* Progress Restriction Modal */}
      {showProgressRestrictionModal && selectedChild && (() => {
        const isPro = selectedChild.has_active_subscription;
        const buttonText = isPro
          ? (t('dashboard.index.children.progress_restriction.upgrade_to_premium') || 'Nâng Cấp Premium')
          : (t('dashboard.index.children.subscription.subscribe') || 'Đăng Ký');
        const message = isPro
          ? (t('dashboard.index.children.progress_restriction.message_pro') || 'Nâng cấp lên gói Premium để xem tiến độ chi tiết.')
          : (t('dashboard.index.children.progress_restriction.message') || 'Bạn cần đăng ký để xem tiến độ chi tiết.');

        return (
          <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title d-flex align-items-center gap-2">
                    <i className="bi bi-lock-fill text-warning"></i>
                    {t('dashboard.index.children.progress_restriction.title')}
                  </h5>
                  <button type="button" className="btn-close" onClick={() => { setShowProgressRestrictionModal(false); setSelectedChild(null); }}></button>
                </div>
                <div className="modal-body">
                  <div className="text-center py-3">
                    <div className="mb-3">
                      <i className="bi bi-graph-up-arrow text-muted" style={{ fontSize: '48px' }}></i>
                    </div>
                    <p className="text-muted mb-0">
                      {message}
                    </p>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => { setShowProgressRestrictionModal(false); setSelectedChild(null); }}
                  >
                    {t('common.close')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-success d-flex align-items-center gap-2"
                    onClick={handleActionFromRestriction}
                    data-testid="progress-restriction-action-button"
                  >
                    <i className={`bi ${isPro ? 'bi-arrow-up' : 'bi-plus-lg'}`}></i>
                    {buttonText}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Subscription History Modal */}
      {showSubscriptionHistoryModal && selectedChild && (
        <SubscriptionHistoryModal
          child={selectedChild}
          onClose={() => { setShowSubscriptionHistoryModal(false); setSelectedChild(null); }}
          onSubscribe={handleSubscribeFromHistory}
          onExtend={handleExtendFromHistory}
          onUpgrade={handleUpgradeFromHistory}
          t={t}
          formatDate={formatDate}
        />
      )}
    </>
  );
};
